import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import { fluid } from 'components/UserDashboardV2/utils/responsive';

const ChevronIcon = ({ direction = "up", onClick, disabled = false }) => {
    // direction can be "up" or "down"
    const transform = direction === "down" ? "rotate(180deg)" : "none";
    return (
        <svg
            width={fluid(18)} height={fluid(16)} viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg"
            style={{ transform, cursor: disabled ? "not-allowed" : "pointer", transition: "transform 0.2s", opacity: disabled ? 0.35 : 1 }}
            onClick={disabled ? undefined : onClick}
        >
            <path d="M2.4197 8.69641L8.08509 3.36308C8.33266 3.12989 8.66015 3 9.0005 3C9.34084 3 9.66832 3.12989 9.91588 3.36308L15.5813 8.69641C15.7757 8.87969 15.9105 9.11699 15.9684 9.37761C16.0262 9.63824 16.0044 9.91019 15.9058 10.1583C15.8071 10.4064 15.6362 10.6193 15.415 10.7693C15.1939 10.9194 14.9328 10.9997 14.6654 11L3.3346 11C3.06723 10.9997 2.80609 10.9194 2.58497 10.7693C2.36384 10.6193 2.1929 10.4064 2.09425 10.1583C1.9956 9.91019 1.9738 9.63824 2.03164 9.37761C2.08949 9.11699 2.2253 8.87969 2.4197 8.69641Z" fill="#AEAEAE"/>
        </svg>
    );
};

const CustomDatePicker = ({
    value,
    onChange,
    placeholder = "Select date",
    disableFuture = false,
    minDate = null,
    maxDate = null,
    variant = "underline",
    popoverPlacement = "left",
    popoverWidth,
    clearable = false,
    onClear,
    disabled = false,
    inputStyle = {},
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isCalendarHovered, setIsCalendarHovered] = useState(false);
    const [placementY, setPlacementY] = useState('bottom');

    const getDefaultDate = useCallback(() => {
        if (value && moment(value).isValid()) {
            return moment(value);
        }
        if (minDate && moment(minDate).isValid()) {
            return moment(minDate);
        }
        return moment();
    }, [value, minDate]);

    const [currentMonth, setCurrentMonth] = useState(() => {
        return (value && moment(value).isValid()) ? moment(value) : (minDate && moment(minDate).isValid() ? moment(minDate) : moment());
    });
    // 'days' (default calendar grid) or 'years' (clicking the year in the
    // header switches to a year-picker grid instead of paging month by
    // month to reach a far-off year).
    const [viewMode, setViewMode] = useState('days');
    const [yearRangeStart, setYearRangeStart] = useState(() => currentMonth.year() - 5);
    const containerRef = useRef(null);
    const popoverRef = useRef(null);

    const updateVerticalPlacement = useCallback(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Actual rendered height or fallback estimate
        const popoverHeight = popoverRef.current ? popoverRef.current.offsetHeight : 295;

        // If it fits below on screen without scrolling, always show below ('bottom')!
        // Only when it does NOT fit below, show above ('top') if there is sufficient room above.
        if (spaceBelow >= popoverHeight + 8) {
            setPlacementY('bottom');
        } else if (spaceAbove > spaceBelow && spaceAbove >= popoverHeight + 8) {
            setPlacementY('top');
        } else if (spaceBelow >= spaceAbove) {
            setPlacementY('bottom');
        } else {
            setPlacementY('top');
        }
    }, []);

    const resetToDefault = useCallback(() => {
        const def = getDefaultDate();
        setCurrentMonth(def.clone());
        setViewMode('days');
        setYearRangeStart(def.year() - 5);
    }, [getDefaultDate]);

    useEffect(() => {
        resetToDefault();
    }, [value, minDate, resetToDefault]);

    useEffect(() => {
        if (isOpen) {
            resetToDefault();
            updateVerticalPlacement();
            window.addEventListener('resize', updateVerticalPlacement);
            return () => {
                window.removeEventListener('resize', updateVerticalPlacement);
            };
        }
    }, [isOpen, resetToDefault, updateVerticalPlacement]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                resetToDefault();
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, resetToDefault]);

    const isDateDisabled = useCallback((date) => {
        if (!date) return false;
        const today = moment();
        if (disableFuture && date.isAfter(today, 'day')) return true;
        if (minDate && moment(minDate).isValid() && date.isBefore(moment(minDate), 'day')) return true;
        if (maxDate && moment(maxDate).isValid() && date.isAfter(moment(maxDate), 'day')) return true;
        return false;
    }, [disableFuture, minDate, maxDate]);

    const today = moment();
    const isNextMonthDisabled = (disableFuture && currentMonth.isSameOrAfter(today, 'month')) ||
        (maxDate && moment(maxDate).isValid() && currentMonth.isSameOrAfter(moment(maxDate), 'month'));

    const isPrevMonthDisabled = minDate && moment(minDate).isValid() && currentMonth.isSameOrBefore(moment(minDate), 'month');

    const handlePrevMonth = (e) => {
        e.stopPropagation();
        if (isPrevMonthDisabled) return;
        setCurrentMonth(prev => prev.clone().subtract(1, 'months'));
    };

    const handleNextMonth = (e) => {
        e.stopPropagation();
        if (isNextMonthDisabled) return;
        setCurrentMonth(prev => prev.clone().add(1, 'months'));
    };

    const handleDateSelect = (date, e) => {
        e.stopPropagation();
        if (isDateDisabled(date)) return;
        onChange(date.format('YYYY-MM-DD'));
        setIsOpen(false);
    };

    useEffect(() => {
        if (disabled && isOpen) {
            setIsOpen(false);
        }
    }, [disabled, isOpen]);

    const toggleOpen = () => {
        if (disabled) return;
        if (!isOpen) {
            resetToDefault();
            updateVerticalPlacement();
        }
        setIsOpen(prev => !prev);
    };

    const handleOpenYearView = (e) => {
        e.stopPropagation();
        setYearRangeStart(currentMonth.year() - 5);
        setViewMode('years');
    };

    const handlePrevYearRange = (e) => {
        e.stopPropagation();
        setYearRangeStart(prev => prev - 12);
    };

    const handleNextYearRange = (e) => {
        e.stopPropagation();
        setYearRangeStart(prev => prev + 12);
    };

    const handleYearSelect = (year, e) => {
        e.stopPropagation();
        if (disableFuture && year > today.year()) return;
        if (minDate && moment(minDate).isValid() && year < moment(minDate).year()) return;
        if (maxDate && moment(maxDate).isValid() && year > moment(maxDate).year()) return;
        setCurrentMonth(prev => prev.clone().year(year));
        setViewMode('days');
    };

    // Generate Calendar Grid
    const generateGrid = () => {
        const startOfMonth = currentMonth.clone().startOf('month');
        const endOfMonth = currentMonth.clone().endOf('month');
        const startDate = startOfMonth.clone().startOf('isoWeek'); // Monday is start of week
        const endDate = endOfMonth.clone().endOf('isoWeek');
        
        const day = startDate.clone().subtract(1, 'day');
        const grid = [];
        
        while (day.isBefore(endDate, 'day')) {
            grid.push(
                Array(7).fill(0).map(() => day.add(1, 'day').clone())
            );
        }
        while (grid.length < 5) {
            grid.push(Array(7).fill(0).map(() => day.add(1, 'day').clone()));
        }
        return grid;
    };

    const grid = generateGrid();
    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            {/* Input display */}
            {variant === 'boxed' ? (
                <div 
                    onClick={toggleOpen}
                    style={{
                        width: '100%',
                        height: fluid(44),
                        padding: `0 ${fluid(14)}`,
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: fluid(15),
                        color: disabled ? '#9CA3AF' : '#1E293B',
                        border: disabled ? '1px solid #E5E7EB' : '1px solid #D1D5DB',
                        borderRadius: fluid(10),
                        backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s, background-color 0.2s',
                        ...inputStyle,
                    }}
                >
                    <span
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: disabled ? '#9CA3AF' : '#1E293B',
                            fontWeight: 500,
                        }}
                    >
                        {value ? moment(value).format('DD MMM YYYY') : placeholder}
                    </span>
                    <div
                        onMouseEnter={() => !disabled && setIsCalendarHovered(true)}
                        onMouseLeave={() => !disabled && setIsCalendarHovered(false)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: fluid(24),
                            height: fluid(24),
                            flexShrink: 0,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {!disabled && clearable && value && isCalendarHovered ? (
                            <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onClear) onClear();
                                    else if (onChange) onChange(null);
                                    setIsCalendarHovered(false);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: fluid(20),
                                    height: fluid(20),
                                    borderRadius: '50%',
                                    backgroundColor: '#CBD5E1',
                                    color: '#334155',
                                    fontSize: fluid(14),
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                }}
                                title="Clear date"
                            >
                                ×
                            </span>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={disabled ? '#9CA3AF' : '#334155'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                        )}
                    </div>
                </div>
            ) : (
                <div 
                    onClick={toggleOpen}
                    style={{
                        width: '100%',
                        padding: `${fluid(8)} 0`,
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: fluid(16),
                        color: disabled ? '#9CA3AF' : (value ? '#1A1A1A' : '#AEAEAE'),
                        borderBottom: disabled ? '1px solid #E5E7EB' : '1px solid #CCC',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        ...inputStyle,
                    }}
                >
                    {value ? moment(value).format('DD MMM YYYY') : placeholder}
                    {/* Minimal calendar icon */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={disabled ? '#9CA3AF' : '#666'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                </div>
            )}

            {/* Calendar Popover */}
            {isOpen && (
                <div
                    ref={popoverRef}
                    style={{
                        position: 'absolute',
                        top: placementY === 'top' ? 'auto' : '100%',
                        bottom: placementY === 'top' ? '100%' : 'auto',
                        marginTop: placementY === 'top' ? 0 : fluid(8),
                        marginBottom: placementY === 'top' ? fluid(8) : 0,
                        left: popoverPlacement === 'right' ? 'auto' : 0,
                        right: popoverPlacement === 'right' ? 0 : 'auto',
                        width: popoverWidth || (variant === 'boxed' ? fluid(340) : fluid(416)),
                        maxWidth: 'calc(100vw - 32px)',
                        height: 'auto',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.16)',
                        borderRadius: fluid(10),
                        zIndex: 1100,
                        padding: `${fluid(14)} ${fluid(18)}`,
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: fluid(12) }}>
                        {viewMode === 'years' ? (
                            <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(16), fontWeight: 400, color: '#191919' }}>
                                {yearRangeStart} - {yearRangeStart + 11}
                            </span>
                        ) : (
                            <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(16), fontWeight: 400, color: '#191919' }}>
                                {currentMonth.format('MMMM, ')}
                                <span
                                    onClick={handleOpenYearView}
                                    style={{ cursor: 'pointer', fontWeight: 700, color: '#594099' }}
                                >
                                    {currentMonth.format('YYYY')}
                                </span>
                            </span>
                        )}
                        <div style={{ display: 'flex', gap: fluid(20) }}>
                            <ChevronIcon
                                direction="up"
                                onClick={viewMode === 'years' ? handlePrevYearRange : handlePrevMonth}
                                disabled={viewMode === 'years' ? (minDate && yearRangeStart < moment(minDate).year()) : isPrevMonthDisabled}
                            />
                            <ChevronIcon
                                direction="down"
                                onClick={viewMode === 'years' ? handleNextYearRange : handleNextMonth}
                                disabled={viewMode === 'years' ? (disableFuture && yearRangeStart + 12 > today.year()) : isNextMonthDisabled}
                            />
                        </div>
                    </div>

                    {viewMode === 'years' ? (
                        /* Year grid — clicking the year in the header above
                           switches here instead of paging month-by-month to
                           reach a far-off year. */
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            rowGap: fluid(14),
                            columnGap: fluid(8),
                            flex: 1,
                            alignContent: 'center',
                        }}>
                            {Array.from({ length: 12 }, (_, i) => yearRangeStart + i).map((year) => {
                                const isSelected = year === currentMonth.year();
                                const isYearDisabled = (disableFuture && year > today.year()) ||
                                    (minDate && moment(minDate).isValid() && year < moment(minDate).year()) ||
                                    (maxDate && moment(maxDate).isValid() && year > moment(maxDate).year());
                                return (
                                    <div
                                        key={year}
                                        onClick={(e) => isYearDisabled ? undefined : handleYearSelect(year, e)}
                                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: isYearDisabled ? 'not-allowed' : 'pointer' }}
                                    >
                                        <div style={{
                                            width: fluid(56),
                                            height: fluid(36),
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            borderRadius: fluid(8),
                                            backgroundColor: isSelected ? '#594099' : 'transparent',
                                            color: isSelected ? '#FFFFFF' : (isYearDisabled ? '#CCCCCC' : '#191919'),
                                            fontFamily: 'Roboto, sans-serif',
                                            fontSize: fluid(16),
                                            fontWeight: isSelected ? 700 : 400,
                                        }}>
                                            {year}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <>
                            {/* Weekdays */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(7, 1fr)',
                                backgroundColor: '#F4F0FF',
                                borderRadius: fluid(8),
                                height: fluid(34),
                                alignItems: 'center',
                                marginBottom: fluid(12) // spacing before grid
                            }}>
                                {weekdays.map(day => (
                                    <div key={day} style={{ textAlign: 'center', fontFamily: 'Roboto, sans-serif', fontSize: fluid(12), fontWeight: 400, color: '#636363' }}>
                                        {day}
                                    </div>
                                ))}
                            </div>

                            {/* Dates */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(7, 1fr)',
                                rowGap: fluid(10), // approximate to fit height
                                columnGap: fluid(6),
                                flex: 1
                            }}>
                                {grid.map((week, weekIdx) =>
                                    week.map((day, dayIdx) => {
                                        const isCurrentMonth = day.month() === currentMonth.month();
                                        const isSelected = value && day.isSame(moment(value), 'day');
                                        const isDisabled = isDateDisabled(day);

                                        return (
                                            <div
                                                key={day.format('YYYY-MM-DD')}
                                                onClick={(e) => handleDateSelect(day, e)}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                <div style={{
                                                    width: fluid(32),
                                                    height: fluid(32),
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    borderRadius: '50%',
                                                    backgroundColor: isSelected ? '#594099' : 'transparent',
                                                    color: isSelected ? '#FFFFFF' : (isDisabled ? '#DDDDDD' : (isCurrentMonth ? '#191919' : '#AEAEAE')),
                                                    fontFamily: 'Roboto, sans-serif',
                                                    fontSize: fluid(16),
                                                    fontWeight: isSelected ? 700 : 400
                                                }}>
                                                    {day.date()}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomDatePicker;
